import { FileTypeEnum } from "api/actor"
import { BorrowerContractFunnelV2Dto } from "api/pricing"
import { useCallback, useMemo } from "react"
import { pricingAPI } from "services/swagger-api"
import { useDocumentViewerDialog } from "components/domain/Document/useDocumentViewerDialog"
import { EmailConfig } from "components/domain/Document/DocumentViewer"
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext"
import { useBorrowerShoppingCart } from "../../contexts/main-borrower-pricing/selectors/selectors"
import { useFunnelDownload } from "./useFunnelDownload"

export const useFunnelCardFooter = (contractFunnel: BorrowerContractFunnelV2Dto) => {
  const { createBorrowerCartItem, deleteBorrowerCartItem } = useMainBorrowerPricingActions()
  const { isDownloading, setIsDownloading } = useFunnelDownload()
  const { isPriceLoading, borrowerId, borrowerProjectId, insured } = useMainBorrowerPricingState()
  const { shoppingCart } = useBorrowerShoppingCart()
  const { openDocumentViewer } = useDocumentViewerDialog()

  const isInShoppingCart = useMemo(() => {
    // Le hash peut être identique entre emprunteurs du même projet (ex: Mutlog),
    // il faut donc aussi vérifier qu'au moins un borrower du funnel courant est
    // dans l'item du panier, sinon on verrait "déjà dans panier" sur le co-emp.
    const currentBorrowerIds = contractFunnel.borrowers.map(b => b.id);
    return shoppingCart.some(item =>
      item.contractFunnel.hash === contractFunnel.hash &&
      item.contractFunnel.borrowers.some(b => currentBorrowerIds.includes(b.id))
    );
  }, [shoppingCart, contractFunnel]);

  const hasBorrowerAlreadyInCart = useMemo(() => {
    if (isInShoppingCart) return false
    const borrowerIdsInFunnel = contractFunnel.borrowers.map(b => b.id)
    return shoppingCart.some(item =>
      item.contractFunnel.borrowers.some(b => borrowerIdsInFunnel.includes(b.id))
    )
  }, [shoppingCart, contractFunnel, isInShoppingCart]);

  const toggleCart = useCallback((borrowerIds: Array<string>, isGroup: boolean) => {
    if (!borrowerId || !borrowerProjectId) {
      return
    }
    if (isInShoppingCart) {
      if (contractFunnel.hash) {
        deleteBorrowerCartItem(borrowerProjectId, contractFunnel.hash)
      }
    } else {
      createBorrowerCartItem(contractFunnel.id, contractFunnel.contractIdentifier, borrowerIds, isGroup, borrowerProjectId, contractFunnel.hash)
    }
  }, [isInShoppingCart, contractFunnel, createBorrowerCartItem, deleteBorrowerCartItem, borrowerId, borrowerProjectId]);

  const viewMNCAPQuote = useCallback(async () => {
    if (!contractFunnel.hash || !borrowerId) {
      return
    }
    setIsDownloading(FileTypeEnum.DevisPdfGenerer)
    try {
      const response = await pricingAPI.generateQuickQuote({ hash: contractFunnel.hash })
      if (response?.storedFileId && insured) {
        const recipientName = [insured.firstName, insured.lastName]
          .filter(Boolean)
          .join(' ')
          .trim()

        openDocumentViewer({
          title: `Devis ${contractFunnel.product.label}`,
          storedFileId: response.storedFileId,
          mimeType: 'application/pdf',
          showDefaultActions: true,
          emailConfig: {
            storedFileId: response.storedFileId,
            risqueAssureId: borrowerId,
            sendTo: insured.email ? [{
              email: insured.email,
              name: recipientName || undefined
            }] : undefined,
            fileTypeInt: FileTypeEnum.DevisPdfGenerer,
            fileName: `Devis_${contractFunnel.product.label}.pdf`
          }
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsDownloading(null)
    }
  }, [setIsDownloading, insured, openDocumentViewer, borrowerId, contractFunnel.hash])

  return {
    isInShoppingCart,
    hasBorrowerAlreadyInCart,
    toggleCart,
    isPriceLoading,
    isDownloading,
    viewMNCAPQuote
  }
}


